import { useState } from 'react';

import {
  SPEICHER_NICHT_DAUERHAFT,
  speicherHinweisNoetig,
  type SpeicherErgebnis,
} from '../speicher-persistenz';

/** Merker in sessionStorage: Karte in dieser Browsersitzung ausgeblendet (Pille bleibt). */
export const SPEICHER_HINWEIS_AUSGEBLENDET = 'bilanx.speicherhinweis.ausgeblendet';

interface Sitzungsspeicher {
  getItem(schluessel: string): string | null;
  setItem(schluessel: string, wert: string): void;
}

function ausgeblendetLesen(speicher: Sitzungsspeicher | null): boolean {
  try {
    return speicher?.getItem(SPEICHER_HINWEIS_AUSGEBLENDET) === '1';
  } catch {
    return false;
  }
}

/** Pille in der Kopfzeile (Muster der übrigen Rückstandshinweise). */
export function SpeicherPille(props: { dauerhaft: SpeicherErgebnis | undefined }) {
  if (!speicherHinweisNoetig(props.dauerhaft)) {
    return null;
  }
  return (
    <span
      className="status-pille warnung"
      title="Der Browser darf die Daten dieser Kasse bei Speicherknappheit löschen – Offline-Verkauf ist gesperrt"
    >
      {SPEICHER_NICHT_DAUERHAFT}
    </span>
  );
}

/**
 * Paket A4: Hinweis mit kurzer Anleitung, wenn der Browser keinen dauerhaften Speicher gewährt.
 * Nicht störend: eine Karte unter der Kopfzeile (wie die Sync-Meldung), für die Sitzung
 * ausblendbar; die Pille in der Kopfzeile bleibt. „Erneut anfordern“ fragt den Browser nach
 * einer Bedienung erneut (manche Browser fragen nur dann).
 */
export function SpeicherHinweis(props: {
  dauerhaft: SpeicherErgebnis | undefined;
  onAnfordern: () => Promise<SpeicherErgebnis>;
  sitzung: Sitzungsspeicher | null;
}) {
  const [ausgeblendet, setAusgeblendet] = useState(() => ausgeblendetLesen(props.sitzung));
  const [laeuft, setLaeuft] = useState(false);
  const [abgelehnt, setAbgelehnt] = useState(false);
  if (!speicherHinweisNoetig(props.dauerhaft) || ausgeblendet) {
    return null;
  }
  return (
    <div
      className="karte"
      role="status"
      aria-label={SPEICHER_NICHT_DAUERHAFT}
      style={{ margin: '0.5rem 1rem 0', padding: '0.5rem 1rem' }}
    >
      <strong>
        {props.dauerhaft === null
          ? 'Dieser Browser bestätigt keinen dauerhaften Speicher.'
          : 'Gerätespeicher nicht dauerhaft.'}
      </strong>{' '}
      Der Browser darf die Daten dieser Kasse (ungeklärte Verkäufe, Belegnummernzähler,
      Lagerbuchungen) bei Speicherknappheit löschen; Offline-Verkauf ist deshalb gesperrt. Abhilfe:
      die Kasse als App installieren (Browsermenü „App installieren“ bzw. „Zum Home-Bildschirm“)
      oder dieser Website dauerhaften Speicher erlauben, dann „Erneut anfordern“. Keine Browserdaten
      löschen.
      {abgelehnt && (
        <span className="hinweis">
          {' '}
          Der Browser hat erneut abgelehnt – bitte zuerst als App installieren.
        </span>
      )}{' '}
      <button
        type="button"
        className="taste zweit"
        disabled={laeuft}
        onClick={() => {
          setLaeuft(true);
          props
            .onAnfordern()
            .then((d) => {
              setAbgelehnt(d !== true);
            })
            .catch(() => {
              setAbgelehnt(true);
            })
            .finally(() => {
              setLaeuft(false);
            });
        }}
      >
        Erneut anfordern
      </button>{' '}
      <button
        type="button"
        className="taste zweit"
        onClick={() => {
          try {
            props.sitzung?.setItem(SPEICHER_HINWEIS_AUSGEBLENDET, '1');
          } catch {
            // Sitzungsspeicher nicht verfügbar – nur für diese Anzeige ausblenden
          }
          setAusgeblendet(true);
        }}
      >
        Ausblenden
      </button>
    </div>
  );
}
